import React, { useEffect, useState, useRef, useCallback } from "react";
import { KatWorkflowtracker } from "@amzn/katal-react";
import { useTranslation } from "react-i18next";
import { type UploadProgress, UploadStatus } from "src/api/interfaces/nest";
import {
  type StepState,
  type WorkflowStep,
  WorkflowStage,
} from "src/api/interfaces/nest/workflow";
import client from "src/api/client";
import { fileToBase64 } from "../../../utils";
import { UPLOAD_FOOTER_AUTO_DISMISS_MS } from "../../../constants";
import "./index.css";

const STAGE_ORDER: WorkflowStage[] = [
  WorkflowStage.UPLOADING,
  WorkflowStage.CONVERTING,
  WorkflowStage.VALIDATING,
  WorkflowStage.PROCESSING,
  WorkflowStage.GETTING_RESULTS,
];

const STAGE_DURATION_MS = 4000;
const FAST_FORWARD_MS = 400;

interface NestUploadProgressFooterProps {
  files: File[];
  useCaseId: string;
  documentTypeId: string;
  onComplete: (results: UploadProgress[]) => void;
}

export function NestUploadProgressFooter({
  files,
  useCaseId,
  documentTypeId,
  onComplete,
}: NestUploadProgressFooterProps) {
  const { t } = useTranslation();
  const [exiting, setExiting] = useState(false);
  const [currentStage, setCurrentStage] = useState<WorkflowStage>(
    WorkflowStage.UPLOADING
  );
  const [hasError, setHasError] = useState(false);
  const [errorStage, setErrorStage] = useState<WorkflowStage | null>(null);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const hasStarted = useRef(false);
  const allApiResolved = useRef(false);
  const currentStageRef = useRef<WorkflowStage>(WorkflowStage.UPLOADING);
  const errorStageRef = useRef<WorkflowStage | null>(null);

  const getStepState = useCallback(
    (stage: WorkflowStage): StepState => {
      const stageIndex = STAGE_ORDER.indexOf(stage);

      if (hasError && errorStage !== null) {
        const errorIndex = STAGE_ORDER.indexOf(errorStage);
        if (stageIndex < errorIndex) return "complete";
        if (stageIndex === errorIndex) return "error";
        return "todo";
      }

      if (currentStage === WorkflowStage.DONE) {
        return "complete";
      }

      const currentIndex = STAGE_ORDER.indexOf(currentStage);
      if (stageIndex < currentIndex) {
        return "complete";
      }
      if (stageIndex === currentIndex) {
        return "in-progress";
      }
      return "todo";
    },
    [currentStage, hasError, errorStage]
  );

  const workflowSteps: WorkflowStep[] = [
    {
      label: t("aic_nest_workflow_uploading"),
      state: getStepState(WorkflowStage.UPLOADING),
      id: "step-uploading",
    },
    {
      label: t("aic_nest_workflow_converting"),
      state: getStepState(WorkflowStage.CONVERTING),
      id: "step-converting",
    },
    {
      label: t("aic_nest_workflow_validating"),
      state: getStepState(WorkflowStage.VALIDATING),
      id: "step-validating",
    },
    {
      label: t("aic_nest_workflow_processing"),
      state: getStepState(WorkflowStage.PROCESSING),
      id: "step-processing",
    },
    {
      label: t("aic_nest_workflow_getting_results"),
      state: getStepState(WorkflowStage.GETTING_RESULTS),
      id: "step-getting-results",
    },
  ];

  const processSingleUpload = useCallback(
    async (upload: UploadProgress): Promise<UploadProgress> => {
      try {
        const fileContent = await fileToBase64(upload.file);
        const testResult = await client.nestSubmit({
          useCaseId,
          fileName: upload.fileName,
          fileContent,
          documentTypeId,
        });

        const done: UploadProgress = {
          ...upload,
          progress: 100,
          status: UploadStatus.DONE,
          testResult,
        };
        setUploads((prev) =>
          prev.map((u) => (u.fileName === upload.fileName ? done : u))
        );
        return done;
      } catch (err: any) {
        if (errorStageRef.current === null) {
          const pinned =
            currentStageRef.current === WorkflowStage.DONE
              ? STAGE_ORDER[STAGE_ORDER.length - 1]
              : currentStageRef.current;
          errorStageRef.current = pinned;
          setErrorStage(pinned);
        }
        setHasError(true);
        const statusCode = err?.response?.status || 500;
        const error = err?.response?.data || {
          code: "500",
          message: { key: "error-unknown" },
        };
        const failed: UploadProgress = {
          ...upload,
          progress: 100,
          status: UploadStatus.ERROR,
          error,
          statusCode,
        };
        setUploads((prev) =>
          prev.map((u) => (u.fileName === upload.fileName ? failed : u))
        );
        return failed;
      }
    },
    [useCaseId, documentTypeId]
  );

  const runWorkflowAnimation = useCallback(async () => {
    for (let i = 0; i < STAGE_ORDER.length; i++) {
      if (
        errorStageRef.current !== null &&
        STAGE_ORDER.indexOf(errorStageRef.current) < i
      ) {
        return;
      }

      setCurrentStage(STAGE_ORDER[i]);
      currentStageRef.current = STAGE_ORDER[i];

      if (allApiResolved.current) {
        await new Promise((resolve) => setTimeout(resolve, FAST_FORWARD_MS));
        continue;
      }

      const start = Date.now();
      while (Date.now() - start < STAGE_DURATION_MS) {
        await new Promise((resolve) => setTimeout(resolve, 200));
        if (allApiResolved.current) {
          break;
        }
        if (
          errorStageRef.current !== null &&
          STAGE_ORDER.indexOf(errorStageRef.current) <= i
        ) {
          break;
        }
      }

      if (
        errorStageRef.current !== null &&
        STAGE_ORDER.indexOf(errorStageRef.current) <= i
      ) {
        return;
      }
    }
    setCurrentStage(WorkflowStage.DONE);
    currentStageRef.current = WorkflowStage.DONE;
  }, []);

  useEffect(() => {
    if (!files.length || hasStarted.current) return;
    hasStarted.current = true;

    const initial: UploadProgress[] = files.map((f) => ({
      fileName: f.name,
      progress: 0,
      status: UploadStatus.UPLOADING,
      file: f,
    }));
    setUploads(initial);

    const uploadsPromise = Promise.all(
      initial.map((u) => processSingleUpload(u))
    );

    uploadsPromise.then(() => {
      allApiResolved.current = true;
    });

    const animationPromise = runWorkflowAnimation();

    Promise.all([uploadsPromise, animationPromise]).then(([results]) => {
      onComplete(results);
    });
  }, [files, processSingleUpload, runWorkflowAnimation, onComplete]);

  const completed = uploads.filter(
    (u) => u.status === UploadStatus.DONE || u.status === UploadStatus.ERROR
  ).length;
  const allDone = uploads.length > 0 && completed === uploads.length;

  useEffect(() => {
    if (allDone) {
      const timer = setTimeout(() => {
        setExiting(true);
      }, UPLOAD_FOOTER_AUTO_DISMISS_MS);
      return () => clearTimeout(timer);
    }
  }, [allDone]);

  return (
    <div
      className={`nest-upload-footer${exiting ? " nest-upload-footer--exiting" : ""}`}
    >
      <div className="nest-upload-footer__header">
        <span className="nest-upload-footer__title">
          {t("aic_nest_upload_progress", { completed, total: uploads.length })}
        </span>
      </div>
      <div className="nest-upload-footer__workflow">
        <KatWorkflowtracker
          mode="custom"
          layout="horizontal"
          responsive={true}
          steps={workflowSteps}
        />
      </div>
      {uploads.length > 0 && (
        <div className="nest-upload-footer__files">
          {uploads.map((upload) => (
            <div key={upload.fileName} className="nest-upload-footer__file">
              <span className="nest-upload-footer__filename">
                {upload.fileName}
              </span>
              <span
                className={`nest-upload-footer__status nest-upload-footer__status--${upload.status}`}
              >
                {upload.status === UploadStatus.DONE &&
                  t("aic_nest_workflow_complete")}
                {upload.status === UploadStatus.ERROR &&
                  t("aic_nest_workflow_error")}
                {upload.status === UploadStatus.UPLOADING &&
                  t("aic_nest_workflow_in_progress")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
